import { Asset, AssetManager, SpriteFrame, __private } from 'cc';
import { assetTypeToAssetNameMap } from '../../Config/AssetLoadingConfigType';
import { joinURL } from '../Utils/Utility';

declare module 'cc' {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    namespace AssetManager {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        interface Bundle {
            preloadAsync<T extends typeof Asset>(assetPath: string, assetType: T, prefixPath?: string): Promise<void>;
            preloadDirAsync<T extends typeof Asset>(
                dirPath: string,
                assetType: T,
                prefixPath?: string,
                onProgress?: __private._cocos_core_asset_manager_shared__ProgressCallback,
            ): Promise<void>;
            loadAsync<T extends typeof Asset>(assetPath: string, assetType: T, prefixPath?: string): Promise<InstanceType<T>>;
            loadDirAsync<T extends typeof Asset>(
                dirPath: string,
                assetType: T,
                prefixPath?: string,
                onProgress?: __private._cocos_core_asset_manager_shared__ProgressCallback,
                onComplete?: __private._cocos_core_asset_manager_shared__CompleteCallbackWithData<InstanceType<T>[]>,
            ): Promise<InstanceType<T>[]>;
        }
    }
}

AssetManager.Bundle.prototype.preloadAsync = async function preloadAsync<T extends typeof Asset>(
    this: AssetManager.Bundle,
    assetPath: string,
    assetType: T,
    prefixPath = '',
): Promise<void> {
    const assetName = assetTypeToAssetNameMap.get(assetType);
    if (!assetName) {
        throw new Error(`unsupported asset type: ${assetType}`);
    }
    let tempAssetPath = joinURL(assetName, assetPath);
    if ((assetType as typeof Asset) === SpriteFrame) {
        tempAssetPath = joinURL(tempAssetPath, 'spriteFrame');
    }
    const completeAssetPath = prefixPath ? joinURL(prefixPath, tempAssetPath) : tempAssetPath;
    return new Promise<void>((resolve, reject) => {
        this.preload(completeAssetPath, assetType, (err) => {
            if (err) {
                reject(err);
            } else {
                resolve();
            }
        });
    });
};

AssetManager.Bundle.prototype.preloadDirAsync = async function preloadDirAsync<T extends typeof Asset>(
    this: AssetManager.Bundle,
    dirPath: string,
    assetType: T,
    prefixPath = '',
    onProgress: __private._cocos_core_asset_manager_shared__ProgressCallback = undefined,
): Promise<void> {
    const assetName = assetTypeToAssetNameMap.get(assetType);
    if (!assetName) {
        throw new Error(`unsupported asset type: ${assetType}`);
    }
    const tempAssetPath = joinURL(assetName, dirPath);
    const completeAssetPath = prefixPath ? joinURL(prefixPath, tempAssetPath) : tempAssetPath;
    return new Promise<void>((resolve, reject) => {
        this.preloadDir(completeAssetPath, assetType, onProgress, (err) => {
            if (err) {
                reject(err);
            }
            resolve();
        });
    });
};

AssetManager.Bundle.prototype.loadAsync = async function loadAsync<T extends typeof Asset>(
    this: AssetManager.Bundle,
    assetPath: string,
    assetType: T,
    prefixPath = '',
): Promise<InstanceType<T>> {
    const assetName = assetTypeToAssetNameMap.get(assetType);
    if (!assetName) {
        throw new Error(`unsupported asset type: ${assetType}`);
    }
    let tempAssetPath = joinURL(assetName, assetPath);
    if ((assetType as typeof Asset) === SpriteFrame) {
        tempAssetPath = joinURL(tempAssetPath, 'spriteFrame');
    }
    const completeAssetPath = prefixPath ? joinURL(prefixPath, tempAssetPath) : tempAssetPath;
    return new Promise<InstanceType<T>>((resolve, reject) => {
        this.load(completeAssetPath, assetType, (err, asset: InstanceType<T>) => {
            if (err) {
                reject(err);
            } else {
                resolve(asset);
            }
        });
    });
};

AssetManager.Bundle.prototype.loadDirAsync = async function loadDirAsync<T extends typeof Asset>(
    this: AssetManager.Bundle,
    dirPath: string,
    assetType: T,
    prefixPath = '',
    onProgress: __private._cocos_core_asset_manager_shared__ProgressCallback = undefined,
    onComplete: __private._cocos_core_asset_manager_shared__CompleteCallbackWithData<InstanceType<T>[]> = undefined,
): Promise<InstanceType<T>[]> {
    const assetName = assetTypeToAssetNameMap.get(assetType);
    if (!assetName) {
        throw new Error(`unsupported asset type: ${assetType}`);
    }
    const tempAssetPath = joinURL(assetName, dirPath);
    const completeAssetPath = prefixPath ? joinURL(prefixPath, tempAssetPath) : tempAssetPath;
    return new Promise<InstanceType<T>[]>((resolve, reject) => {
        this.loadDir(completeAssetPath, assetType, onProgress, (err, assets: InstanceType<T>[]) => {
            onComplete?.(err, assets);
            if (err) {
                reject(err);
            }
            resolve(assets);
        });
    });
};

export {};
